import doeBanner from '@/components/svg/images/doeBanner.svg';
import { GlobalsCompanyInfo, GlobalsFooterNav, GlobalsFooterNavNavGroup, GlobalsSocials } from '@/tina/__generated__/types';
import * as stylex from '@stylexjs/stylex';
import Image from 'next/image';
import { tinaField } from 'tinacms/dist/react';
import { a11y } from '../styles/a11y';
import { layout } from '../styles/layout';
import { calloutTypographyColors } from '../styles/themes';
import '../styles/tokens.stylex';
import { color, columnWidth, size, spacing, typographyColor } from '../styles/tokens.stylex';
import { typography } from '../styles/typography';
import { Button } from './Buttons';
import { CompanyLogo } from './Logo';
import { DynamicFooterLayout } from './MainNavigation/ClientComponents';
import { NextLinkWithEventPayload } from './NextLinkWithEventPayload';
import { OpenCookieSettingsButton } from './CookieBanner/notificationPopoverClientUi';
import { UiIcon, UiIconProps } from './svg/UiIcon';
export interface FooterProps {
  navigation?: GlobalsFooterNav[];
  companyInfo?: GlobalsCompanyInfo;
  socials?: GlobalsSocials[];
  showTelephoneNumber?: boolean;
  bottomSpacer?: boolean;
}
const SOCIAL_LOGO_LOOKUP = {
  Facebook: 'FacebookIcon',
  Instagram: 'InstagramIcon',
  LinkedIn: 'LinkedinIcon'
};
export function Footer({
  navigation,
  companyInfo,
  socials,
  showTelephoneNumber,
  bottomSpacer
}: FooterProps) {
  return <footer {...stylex.props([calloutTypographyColors, layout.sectionWrapper, sx.root, bottomSpacer && sx.bottomSpacer])}>
      <div {...stylex.props([layout.maxWidthWrapper, sx.columnLayout])}>
        <section className="x16pmaob xrvj5dj x619ttb">
          <div className="x16pmaob x1cy8zhl x78zum5 xdt5ytf x1fzhlzt xf0nrvi x8wndk4">
            <CompanyLogo color="white" />
            {companyInfo?.slogan && <p {...stylex.props([typography.body, sx.slogan])} data-tina-field={tinaField(companyInfo, 'slogan')}>
                {companyInfo.slogan}
              </p>}
          </div>
          {showTelephoneNumber && companyInfo?.telephoneNumber && <Button variant="ghost" iconLeft={<UiIcon icon="Phone" />} leftAlign outdentLeftPadding data-tina-field={tinaField(companyInfo, 'telephoneNumber')} asChild>
              <NextLinkWithEventPayload clickEventPayload={{
            event: 'phone_call_click',
            location: 'footer'
          }} href={`tel:${companyInfo?.telephoneNumberLink}`}>
                {companyInfo.telephoneNumber}
              </NextLinkWithEventPayload>
            </Button>}
          {companyInfo?.address1 && <Button asChild variant="ghost" leftAlign outdentLeftPadding data-tina-field={tinaField(companyInfo, 'address1')}>
              <NextLinkWithEventPayload href="https://maps.app.goo.gl/Xp8Dm63Bh7FXzAXdA?g_st=ic">
                {companyInfo.address1}
                {companyInfo.address2 && <>
                    <br />
                    {companyInfo.address2}
                  </>}
                {companyInfo.address3 && <>
                    <br />
                    {companyInfo.address3}
                  </>}
              </NextLinkWithEventPayload>
            </Button>}
        </section>
        {navigation && <DynamicFooterLayout>
            <nav aria-label="Footer links">
              <h1 {...stylex.props([a11y.visuallyHidden])}>Site Links</h1>
              <ul className="x1ghz6dp x1717udv xe8uvvx xdt5ytf x1mhrlx8 xrvj5dj x15vz560">
                {navigation.map((group, i) => {
              const key = `${i}-${group?.label.toLocaleLowerCase().split(/ /g).join('-')}`;
              return <li key={key}>
                      <FooterNavGroup data={group} />
                    </li>;
            })}
              </ul>
            </nav>
          </DynamicFooterLayout>}
        {socials && <div className="x16pmaob x78zum5 x1a02dak xx1abn8 xcrlgei x619ttb">
            {socials.map(social => {
          return <Button asChild variant="ghost" iconLeft={<UiIcon icon={SOCIAL_LOGO_LOOKUP[(social.platform || 'Facebook') as keyof typeof SOCIAL_LOGO_LOOKUP] as UiIconProps['icon']} />} key={social.handle?.split(/ /g).join('-')} data-tina-field={tinaField(social, 'handle')} outdentLeftPadding>
                  <NextLinkWithEventPayload href={social.link || '#'} clickEventPayload={{
              event: 'social_media_click',
              handle: social.handle,
              platform: social.platform,
              location: 'footer'
            }}>
                    {social.handle}
                  </NextLinkWithEventPayload>
                </Button>;
        })}
          </div>}
        <div className="xx1abn8 xcrlgei">
          <OpenCookieSettingsButton outdentLeftPadding />
        </div>
        <div className="x16pmaob xrvj5dj x619ttb x17zmmep">
          <Image src={doeBanner} alt="A&O proudly supports the Duke of Edinburgh's International Award, Australia." height={64} />
        </div>
      </div>
    </footer>;
}
interface FooterNavGroupProps {
  data: GlobalsFooterNav;
}
function FooterNavGroup({
  data
}: FooterNavGroupProps) {
  return <div className="x3ybb33 xrvj5dj">
      <h2 {...stylex.props([typography.heading4, sx.navGroupHeading])} data-tina-field={tinaField(data, 'label')}>
        {data.label}
      </h2>
      <ul className="x1ghz6dp x1717udv x3ybb33 xe8uvvx x78zum5 xdt5ytf">
        {data?.navGroup?.map((item, i) => {
        const key = `${i}-${item?.label.toLocaleLowerCase().split(/ /g).join('-')}`;
        return <li key={key}>
              <NextLinkWithEventPayload {...stylex.props([typography.body, sx.navLink])} href={`/${item?.link._sys.breadcrumbs.join('/')}`} data-tina-field={tinaField(item as GlobalsFooterNavNavGroup, 'label')} clickEventPayload={{
            event: 'navigation_link_click',
            location: 'footer',
            href: item?.link._sys.breadcrumbs.join('/')
          }}>
                {item?.label}
              </NextLinkWithEventPayload>
            </li>;
      })}
      </ul>
    </div>;
}
const sx = {
  root: {
    kWkggS: "x97tsy9",
    $$css: true
  },
  columnLayout: {
    kOIVth: "x1mhrlx8",
    kGNEyG: "x1pha0wt",
    k1xSpc: "xrvj5dj",
    kumcoG: "x1y6fwsi xmp4bxo",
    kVAEAm: "x1n2onr6",
    $$css: true
  },
  slogan: {
    ks0D6T: "x1j1zfo6",
    $$css: true
  },
  navGroupHeading: {
    kMwMTN: "xrcl841",
    k1K539: "xahult9",
    $$css: true
  },
  navLink: {
    kybGjl: "x1hl2dhg",
    kMwMTN: "x1joeokn x1j61h1e",
    $$css: true
  },
  bottomSpacer: {
    kGO01o: "x1dqhp50",
    $$css: true
  }
};